import { useMemo } from 'react';
import { Radio, Ear, ArrowRight } from 'lucide-react';
import { Card } from '@reflex/ui/components/ui/card';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionLabel, PageDescription, PanelTitle } from '@reflex/ui/components/layout/Typography';
import { usePlugins } from '@/hooks/config/usePlugins';

interface ProviderWiring {
  pluginName: string;
  source: string;
  route: string;
  events: Array<{ event: string; description?: string }>;
  receivers: Array<{ pluginName: string; events: string[] }>;
}

export function WebhookWiringSection() {
  const { manifests } = usePlugins();

  const wirings = useMemo<ProviderWiring[]>(() => {
    const providers = manifests.filter((m) => m.enabled !== false && m.server?.webhookProvider);
    return providers.map((p) => {
      const provider = p.server!.webhookProvider!;
      const receiverMap = new Map<string, Set<string>>();
      for (const m of manifests) {
        if (m.enabled === false || !m.server?.webhookReceivers) continue;
        for (const r of m.server.webhookReceivers) {
          if (r.source !== provider.source) continue;
          const existing = receiverMap.get(m.name);
          if (existing) {
            r.events.forEach((e) => existing.add(e));
          } else {
            receiverMap.set(m.name, new Set(r.events));
          }
        }
      }
      const receivers = [...receiverMap].map(([pluginName, events]) => ({
        pluginName,
        events: [...events],
      }));
      return {
        pluginName: p.name,
        source: provider.source,
        route: provider.route,
        events: provider.events,
        receivers,
      };
    });
  }, [manifests]);

  if (wirings.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      <SectionLabel>Registered providers &amp; receivers</SectionLabel>
      {wirings.map((wiring) => (
        <Card key={wiring.source} className="p-4">
          <div className="space-y-3">
            <div className="flex min-w-0 items-center gap-2">
              <Radio className="h-4 w-4 shrink-0 text-muted-foreground" />
              <PanelTitle as="h3" size="sm">
                {wiring.pluginName}
              </PanelTitle>
              <Badge variant="outline" className="text-xs font-normal">
                {wiring.source}
              </Badge>
              <span className="text-xs text-muted-foreground font-mono">{wiring.route}</span>
            </div>

            <div className="flex flex-wrap gap-1">
              {wiring.events.map((e) => (
                <Badge key={e.event} variant="secondary" className="text-xs font-normal">
                  {e.event}
                </Badge>
              ))}
            </div>

            {wiring.receivers.length > 0 ? (
              <div className="space-y-1.5 pl-6 border-l-2 border-border">
                {wiring.receivers.map((r) => (
                  <div key={r.pluginName} className="flex min-w-0 items-center gap-2 text-xs">
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <Ear className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <PanelTitle as="span" size="sm" className="text-xs">
                      {r.pluginName}
                    </PanelTitle>
                    <span className="text-muted-foreground">
                      listens to {r.events.map((e) => `${wiring.source}.${e}`).join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <PageDescription className="text-xs pl-6 mt-0">
                No receivers registered
              </PageDescription>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
